import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ShieldCheck, RefreshCw, FlaskConical, Bot, Users, Globe, Route as RouteIcon } from "lucide-react";
import { toast } from "sonner";
import { getTrafficQuality, type TrafficQualityResult } from "@/lib/admin-traffic.functions";

export const Route = createFileRoute("/_authenticated/admin/traffic")({
  component: AdminTrafficPage,
  head: () => ({ meta: [{ title: "Traffic Quality — Platform Admin" }, { name: "robots", content: "noindex" }] }),
});

const RANGES = [7, 14, 30, 90] as const;
type Audience = "human" | "all" | "bot";

function pct(n: number) {
  return `${n.toFixed(1)}%`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function AdminTrafficPage() {
  const { isPlatformAdmin } = useAuth();
  const fetchTraffic = useServerFn(getTrafficQuality);
  const [days, setDays] = useState<number>(30);
  const [audience, setAudience] = useState<Audience>("human");
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState<TrafficQualityResult | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTraffic({ data: { days, audience, includeTestAccounts: includeTest } });
      setData(res);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to load traffic quality");
    } finally {
      setLoading(false);
    }
  }, [fetchTraffic, days, audience, includeTest]);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    void load();
  }, [isPlatformAdmin, load]);

  if (!isPlatformAdmin) {
    return (
      <div className="px-4 py-10 md:px-8">
        <Card className="mx-auto max-w-lg p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Traffic quality is reserved for the platform owner.
          </p>
        </Card>
      </div>
    );
  }

  const maxPathSessions = Math.max(1, ...(data?.by_path ?? []).map((p) => p.sessions));

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-primary">Platform Admin</p>
        <h1 className="font-display text-2xl font-bold md:text-3xl">Traffic quality</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          First-party sessions, split into humans and machines using capture-time signals.
          Hosting analytics counts every request before our scripts run, so its numbers stay higher —
          this page is the source of truth for human traffic and bounce rate.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-md border border-border">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={`px-3 py-2 text-xs font-semibold ${days === r ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
            >
              {r}d
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-md border border-border">
          {(["human", "all", "bot"] as Audience[]).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAudience(a)}
              className={`px-3 py-2 text-xs font-semibold capitalize ${audience === a ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
            >
              {a === "human" ? "Humans" : a === "all" ? "All" : "Bots"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch id="traffic-test" checked={includeTest} onCheckedChange={setIncludeTest} />
          <Label htmlFor="traffic-test" className="flex items-center gap-1 text-xs">
            <FlaskConical className="h-3.5 w-3.5" /> Include test accounts
          </Label>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {!data ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {loading ? "Loading…" : "No data for this range."}
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Sessions (all)" value={String(data.sessions)} hint={`since ${new Date(data.since).toLocaleDateString()}`} />
            <Stat label="Human sessions" value={String(data.human_sessions)} hint={`${pct(data.human_share)} of all traffic`} />
            <Stat label="Bot sessions" value={String(data.bot_sessions)} hint="headless, scanners, previewers" />
            <Stat label="Bounce rate" value={pct(data.bounce_rate)} hint="1 page view, no engagement" />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Pages / session" value={data.pages_per_session.toFixed(2)} />
            <Stat label="Engaged sessions" value={String(data.engaged_sessions)} hint="dwell, scroll or interaction" />
            <Stat label="Page views" value={String(data.page_views)} />
            <Stat label="Signups" value={String(data.signups)} />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Card className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-display text-sm font-semibold">Bot traffic by reason</h3>
              </div>
              {data.by_bot_reason.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No bot sessions detected in this range.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.by_bot_reason.map((r) => (
                    <li key={r.reason} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                      <span className="min-w-0 truncate font-mono text-xs">{r.reason}</span>
                      <span className="font-semibold">{r.sessions}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-display text-sm font-semibold">Referrers (humans only)</h3>
              </div>
              {data.by_referrer.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No human sessions in this range.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.by_referrer.map((r) => (
                    <li key={r.referrer} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                      <span className="min-w-0 truncate">{r.referrer}</span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-muted-foreground">bounce {pct(r.bounce_rate)}</span>
                        <span className="font-semibold">{r.human_sessions}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card className="mt-4 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <RouteIcon className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-sm font-semibold">Landing paths</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Path</th>
                    <th className="px-4 py-2 text-right font-medium">Sessions</th>
                    <th className="px-4 py-2 text-right font-medium">Humans</th>
                    <th className="px-4 py-2 text-right font-medium">Human share</th>
                    <th className="px-4 py-2 text-right font-medium">Bounce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.by_path.map((p) => (
                    <tr key={p.path}>
                      <td className="max-w-[18rem] px-4 py-2">
                        <div className="truncate font-mono text-xs">{p.path}</div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                          <div
                            className="h-1.5 rounded-full bg-primary"
                            style={{ width: `${Math.round((p.sessions / maxPathSessions) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">{p.sessions}</td>
                      <td className="px-4 py-2 text-right">{p.human_sessions}</td>
                      <td className="px-4 py-2 text-right">{pct(p.human_share)}</td>
                      <td className="px-4 py-2 text-right">{pct(p.bounce_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Sessions recorded before bot classification shipped count as human — they show as
            &quot;unclassified&quot; rather than being dropped, so history stays intact.
          </p>
        </>
      )}
    </div>
  );
}
